import { cx } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { TransitionGroup } from 'react-transition-group';
import { useAppContext } from '@provider/app-context';
import { breakpoints } from '@ui/css';
import { formatTimestamp } from '@utils/common';
import { useMatchBreakpoint, useActivityIndicator, CSSRefTransition, Chevron } from '@utils/react';
import { EmptyMessage } from '../../../atoms/EmptyMessage/EmptyMessage.js';
import { Table } from '../../../atoms/Table/Table.js';
import { Loader } from '../../../atoms/loader.js';
import { getStatusForProductQuery } from '../api.get-status.js';
import { selectSortedStatusEntries } from '../state.selectors.js';
import { statusStore } from '../state.store.js';
import { StatusHeader } from './atom.header.js';
import { StatusErrorMessage } from './message.error.js';
import { ENTER_TRANSITION_DURATION_MS, transitionCls, statusCls, content, tHeadingTrCls, tHeadingChevronCls, tHeadingCls, tSortHeadingCls, tDateCls, tTitleCls, tUrlCls, tCellCls, tClampCls, tLinkCls } from './page.style.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

const Status = () => {
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const sortDirection = statusStore.useSelector(state => state.direction);
  const sortMethod = statusStore.useSelector(state => state.sortedBy);
  const productId = useAppContext(state => state.product?.isbn);
  const {
    data,
    isError,
    isLoading
  } = useQuery({
    ...getStatusForProductQuery(productId),
    // data fetching must have been handled before displaying the page
    enabled: false
  });
  const activityIndicator = useActivityIndicator(isLoading, {
    allowInitial: true
  });
  const entries = statusStore.useSelector(state => selectSortedStatusEntries(state, data));
  const rows = entries.map(it => ({
    model: it
  }));
  const onDateSort = () => {
    statusStore.dispatch(statusStore.actions.sort("date"));
  };
  const onTitleSort = () => {
    statusStore.dispatch(statusStore.actions.sort("title"));
  };
  const contentKey = `${sortDirection}-${sortMethod}`;
  const rowHeight = isMobile ? 150 : 80;
  const headerHeight = isMobile ? 35 : 44;
  return /*#__PURE__*/jsx(TransitionGroup, {
    component: null,
    children: activityIndicator.isActive() ? /*#__PURE__*/jsx(CSSRefTransition, {
      classNames: transitionCls,
      timeout: ENTER_TRANSITION_DURATION_MS,
      children: ref => /*#__PURE__*/jsx(Loader, {
        ref: ref
      })
    }, "loader") : /*#__PURE__*/jsx(CSSRefTransition, {
      classNames: transitionCls,
      timeout: ENTER_TRANSITION_DURATION_MS,
      children: ref => /*#__PURE__*/jsxs("div", {
        ref: ref,
        className: statusCls,
        children: [/*#__PURE__*/jsx(StatusHeader, {}), /*#__PURE__*/jsx(TransitionGroup, {
          className: content,
          children: (() => {
            if (isError) {
              return /*#__PURE__*/jsx(CSSRefTransition, {
                classNames: transitionCls,
                timeout: ENTER_TRANSITION_DURATION_MS,
                children: ref => /*#__PURE__*/jsx(StatusErrorMessage, {
                  ref: ref
                })
              }, "error");
            }
            if (entries.length === 0) {
              return /*#__PURE__*/jsx(CSSRefTransition, {
                classNames: transitionCls,
                timeout: ENTER_TRANSITION_DURATION_MS,
                children: ref => /*#__PURE__*/jsx(EmptyMessage, {
                  ref: ref
                })
              }, "empty");
            }
            return /*#__PURE__*/jsx(CSSRefTransition, {
              classNames: transitionCls,
              timeout: ENTER_TRANSITION_DURATION_MS,
              children: ref => /*#__PURE__*/jsx("div", {
                ref: ref,
                children: /*#__PURE__*/jsx(Table, {
                  contentKey: contentKey,
                  rows: rows,
                  rowHeight: rowHeight,
                  headerHeight: headerHeight,
                  renderRow: renderRow,
                  children: /*#__PURE__*/jsx("thead", {
                    children: /*#__PURE__*/jsxs("tr", {
                      className: tHeadingTrCls,
                      children: [/*#__PURE__*/jsxs("th", {
                        className: cx(tHeadingCls, tSortHeadingCls, tDateCls, sortMethod === "date" && "active"),
                        onClick: onDateSort,
                        children: [isMobile ? "Dato/Beskrivelse" : "Dato", /*#__PURE__*/jsx("span", {
                          className: tHeadingChevronCls,
                          children: /*#__PURE__*/jsx(Chevron, {
                            vertical: true,
                            inversed: sortMethod === "date" && sortDirection === "desc",
                            size: 6,
                            lineWidth: 1
                          })
                        })]
                      }), !isMobile && /*#__PURE__*/jsxs("th", {
                        className: cx(tHeadingCls, tSortHeadingCls, tTitleCls, sortMethod === "title" && "active"),
                        onClick: onTitleSort,
                        children: ["Beskrivelse", /*#__PURE__*/jsx("span", {
                          className: tHeadingChevronCls,
                          children: /*#__PURE__*/jsx(Chevron, {
                            vertical: true,
                            inversed: sortMethod === "title" && sortDirection === "desc",
                            size: 6,
                            lineWidth: 1
                          })
                        })]
                      }), !isMobile && /*#__PURE__*/jsx("th", {
                        className: cx(tHeadingCls, tUrlCls),
                        children: "Information"
                      })]
                    })
                  })
                })
              })
            }, "content");
          })()
        })]
      })
    }, "content")
  });
};
function renderRow({
  model
}) {
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("td", {
      className: cx(tCellCls, tDateCls),
      children: formatTimestamp(model.createdAt)
    }), /*#__PURE__*/jsx("td", {
      className: cx(tCellCls, tTitleCls),
      children: /*#__PURE__*/jsx("div", {
        className: tClampCls,
        children: model.title
      })
    }), /*#__PURE__*/jsx("td", {
      className: cx(tCellCls, tUrlCls),
      children: /*#__PURE__*/jsx("a", {
        className: tLinkCls,
        href: model.url,
        target: "_blank",
        rel: "noreferrer",
        children: "L\xE6s mere"
      })
    })]
  });
}

export { Status };
//# sourceMappingURL=page.js.map
